import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Plus, GripVertical, Clock, User, Pencil, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AgendaTab({ meetingId, meeting, members }) {
  const { api, isChair } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    duration_minutes: 30,
    facilitator_id: '',
  });

  const fetchItems = useCallback(async () => {
    try {
      const response = await api().get(`/meetings/${meetingId}/agenda`);
      setItems(response.data);
    } catch (error) {
      console.error('Failed to fetch agenda:', error);
      toast.error('Failed to load agenda');
    } finally {
      setLoading(false);
    }
  }, [api, meetingId]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleSaveItem = async () => {
    if (!formData.title) {
      toast.error('Title is required');
      return;
    }

    setSaving(true);
    try {
      if (editingItem) {
        const response = await api().put(`/meetings/${meetingId}/agenda/${editingItem.id}`, formData);
        setItems(items.map(item => item.id === editingItem.id ? response.data : item));
        toast.success('Item updated');
      } else {
        const response = await api().post(`/meetings/${meetingId}/agenda`, formData);
        setItems([...items, response.data]);
        toast.success('Item added');
      }
      handleCloseDialog();
    } catch (error) {
      toast.error('Failed to save item');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await api().delete(`/meetings/${meetingId}/agenda/${itemId}`);
      setItems(items.filter(item => item.id !== itemId));
      toast.success('Item deleted');
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination || !isChair) return;

    const reordered = Array.from(items);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);
    
    setItems(reordered);

    try {
      await api().put(`/meetings/${meetingId}/agenda/reorder`, reordered.map(item => item.id));
    } catch (error) {
      toast.error('Failed to reorder items');
      fetchItems();
    }
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title,
      description: item.description || '',
      duration_minutes: item.duration_minutes,
      facilitator_id: item.facilitator_id || '',
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingItem(null);
    setFormData({
      title: '',
      description: '',
      duration_minutes: 30,
      facilitator_id: '',
    });
  };

  const calculateStartTime = (index) => {
    // Assume meeting starts at 18:30 (6:30 PM)
    let totalMinutes = 18 * 60 + 30;
    for (let i = 0; i < index; i++) {
      totalMinutes += items[i].duration_minutes;
    }
    const hours = Math.floor(totalMinutes / 60) % 24;
    const minutes = totalMinutes % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  const getMemberName = (memberId) => {
    const member = members.find(m => m.id === memberId);
    return member?.name || 'Unassigned';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="agenda-tab">
      {isChair && (
        <div className="flex justify-end">
          <Dialog open={dialogOpen} onOpenChange={(open) => open ? setDialogOpen(true) : handleCloseDialog()}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="add-agenda-item-btn">
                <Plus className="h-4 w-4" />
                Add Item
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingItem ? 'Edit Agenda Item' : 'Add Agenda Item'}</DialogTitle>
                <DialogDescription>
                  {editingItem ? 'Update the agenda item details.' : 'Add a new item to the meeting agenda.'}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="item-title">Title *</Label>
                  <Input
                    id="item-title"
                    placeholder="e.g., Welcome and Introduction"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    data-testid="agenda-item-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="item-duration">Duration (minutes)</Label>
                  <Input
                    id="item-duration"
                    type="number"
                    min={5}
                    max={180}
                    value={formData.duration_minutes}
                    onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) || 30 })}
                    data-testid="agenda-item-duration-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="item-facilitator">Facilitator</Label>
                  <Select
                    value={formData.facilitator_id || "unassigned"}
                    onValueChange={(value) => setFormData({ ...formData, facilitator_id: value === "unassigned" ? "" : value })}
                  >
                    <SelectTrigger data-testid="agenda-item-facilitator-select">
                      <SelectValue placeholder="Select facilitator" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {members.map((member) => (
                        <SelectItem key={member.id} value={member.id}>
                          {member.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="item-description">Description</Label>
                  <Textarea
                    id="item-description"
                    placeholder="Optional description..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    data-testid="agenda-item-description-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={handleCloseDialog}>
                  Cancel
                </Button>
                <Button onClick={handleSaveItem} disabled={saving} data-testid="save-agenda-item-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {editingItem ? 'Update' : 'Add'} Item
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {items.length === 0 ? (
        <Card className="p-12">
          <div className="empty-state">
            <Clock className="h-12 w-12 mb-4 text-muted-foreground/50" />
            <h3 className="text-lg font-semibold mb-2">No agenda items</h3>
            <p className="text-muted-foreground">
              {isChair ? 'Add items to build your meeting agenda.' : 'The agenda has not been set up yet.'}
            </p>
          </div>
        </Card>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="agenda">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-0">
                {items.map((item, index) => (
                  <Draggable 
                    key={item.id} 
                    draggableId={item.id} 
                    index={index}
                    isDragDisabled={!isChair}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={`timeline-item ${snapshot.isDragging ? 'opacity-50' : ''}`}
                        data-testid={`agenda-item-${index}`}
                      >
                        <Card className="hover:shadow-md transition-shadow">
                          <CardContent className="p-4">
                            <div className="flex items-start gap-3">
                              {isChair && (
                                <div
                                  {...provided.dragHandleProps}
                                  className="mt-1 cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
                                >
                                  <GripVertical className="h-5 w-5" />
                                </div>
                              )}
                              
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                  <div>
                                    <h3 className="font-semibold">{item.title}</h3>
                                    {item.description && (
                                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                                        {item.description}
                                      </p>
                                    )}
                                  </div>
                                  
                                  {isChair && (
                                    <div className="flex items-center gap-1 shrink-0">
                                      <Button 
                                        variant="ghost" 
                                        size="icon" 
                                        className="h-8 w-8"
                                        onClick={() => handleEditItem(item)}
                                        data-testid={`edit-agenda-item-${index}`}
                                      >
                                        <Pencil className="h-4 w-4" />
                                      </Button>
                                      <Button 
                                        variant="ghost" 
                                        size="icon" 
                                        className="h-8 w-8 text-destructive hover:text-destructive"
                                        onClick={() => handleDeleteItem(item.id)}
                                        data-testid={`delete-agenda-item-${index}`}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  )}
                                </div>
                                
                                <div className="flex flex-wrap items-center gap-3 mt-3">
                                  <Badge variant="outline" className="font-mono text-xs">
                                    <Clock className="h-3 w-3 mr-1" />
                                    {calculateStartTime(index)} · {item.duration_minutes} min
                                  </Badge>
                                  
                                  {item.facilitator_id && (
                                    <Badge variant="secondary" className="text-xs">
                                      <User className="h-3 w-3 mr-1" />
                                      {getMemberName(item.facilitator_id)}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </div>
  );
}
